import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import PartnerGate from "./Gate";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";

const LEAD_STAGES = ["lead", "contacted", "meeting", "proposal", "negotiation", "agreement", "active_client", "lost"];
const EMPTY_LEAD = { company: "", contact: "", email: "", notes: "", expected_value: "", next_follow_up: "" };

export default function Crm() {
  const [leads, setLeads] = useState([]);
  const [leadModal, setLeadModal] = useState(false);
  const [lead, setLead] = useState(EMPTY_LEAD);

  const load = () => api.get("/partner/leads").then(({ data }) => setLeads(data.leads)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const addLead = async () => {
    try {
      await api.post("/partner/leads", { ...lead, expected_value: Number(lead.expected_value || 0) });
      toast.success("Lead added to CRM");
      setLeadModal(false);
      setLead(EMPTY_LEAD);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const moveLead = async (id, stage) => {
    try {
      await api.patch(`/partner/leads/${id}`, { stage });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <PartnerGate>
      {(overview) => (
        <div data-testid="partner-crm">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2 text-sm" data-testid="crm-pipeline-value">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <span className="text-muted-foreground">Open pipeline value:</span>
              <span className="font-heading font-bold text-[#0B1B3D]">{inr(overview.pipeline_value || 0)}</span>
            </div>
            <Button onClick={() => setLeadModal(true)} className="rounded-full bg-[#0B1B3D]" data-testid="add-lead-btn">Add Lead</Button>
          </div>
          {leads.length === 0 ? (
            <EmptyState title="No employer leads yet" description="Track prospective employer clients from first contact to active client." testId="leads-empty" />
          ) : (
            <div className="space-y-3">
              {leads.map((l) => (
                <div key={l.id} className="bg-white border border-border/60 rounded-xl p-5 flex flex-wrap items-center justify-between gap-4" data-testid={`lead-${l.id}`}>
                  <div>
                    <p className="font-semibold text-sm text-[#0B1B3D]">{l.company}</p>
                    <p className="text-xs text-muted-foreground mt-1">{l.contact} {l.email && `· ${l.email}`}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {l.expected_value ? `Est. value ${inr(l.expected_value)}` : "No estimate"}
                      {l.next_follow_up && ` · Follow up ${fmtDate(l.next_follow_up)}`}
                    </p>
                  </div>
                  <Select value={l.stage} onValueChange={(v) => moveLead(l.id, v)}>
                    <SelectTrigger className="w-44 capitalize" data-testid={`lead-stage-${l.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEAD_STAGES.map((s) => (
                        <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          <Dialog open={leadModal} onOpenChange={setLeadModal}>
            <DialogContent aria-describedby={undefined} data-testid="lead-dialog">
              <DialogHeader><DialogTitle>Add employer lead</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Company *</Label><Input value={lead.company} onChange={(e) => setLead({ ...lead, company: e.target.value })} className="mt-1.5" data-testid="lead-company" /></div>
                <div><Label>Contact person</Label><Input value={lead.contact} onChange={(e) => setLead({ ...lead, contact: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Email</Label><Input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="mt-1.5" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Expected value (₹)</Label><Input type="number" value={lead.expected_value} onChange={(e) => setLead({ ...lead, expected_value: e.target.value })} className="mt-1.5" data-testid="lead-value" /></div>
                  <div><Label>Next follow-up</Label><Input type="date" value={lead.next_follow_up} onChange={(e) => setLead({ ...lead, next_follow_up: e.target.value })} className="mt-1.5" data-testid="lead-follow-up" /></div>
                </div>
                <div><Label>Notes</Label><Textarea rows={2} value={lead.notes} onChange={(e) => setLead({ ...lead, notes: e.target.value })} className="mt-1.5" /></div>
                <Button onClick={addLead} disabled={!lead.company} className="w-full rounded-full bg-[#0B1B3D]" data-testid="lead-submit-btn">Add Lead</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </PartnerGate>
  );
}
